import { createFileRoute, Link } from "@tanstack/react-router";
import { BusinessCaseButton } from "@/components/site/BusinessCaseButton";
import { PageHero } from "@/components/site/PageHero";
import homeHeroPhotoSrc from "@/assets/home-hero-v2.webp";
import heroPeopleAssetSrc from "@/assets/home2d-hero1.png";
const heroPeopleAsset = { url: heroPeopleAssetSrc };
import heroPeople2AssetSrc from "@/assets/Josh.png";
const heroPeople2Asset = { url: heroPeople2AssetSrc };
import heroPeople3AssetSrc from "@/assets/Colleen.png";
const heroPeople3Asset = { url: heroPeople3AssetSrc };
import heroPeople4AssetSrc from "@/assets/home2e-hero1.png";
const heroPeople4Asset = { url: heroPeople4AssetSrc };
import muhammadSrc from "@/assets/Muhammad.png";
import aimeeSrc from "@/assets/Aimee.png";
import alveeraSrc from "@/assets/Alveera.png";
import ayushiSrc from "@/assets/Ayushi.png";
import courtneySrc from "@/assets/Courtney.png";
import jakaSrc from "@/assets/Jaka.png";
import pendiSrc from "@/assets/Pendi.png";
import kanniSrc from "@/assets/Kanni.png";
import nischithaSrc from "@/assets/Nischitha.png";
import jonathanSrc from "@/assets/Jonathan.png";
import patSrc from "@/assets/Pat.png";
import anitaSrc from "@/assets/Anita.png";
import sushilSrc from "@/assets/Sushil.png";
import derekSrc from "@/assets/Derek.png";

import leadershipGapBgAssetSrc from "@/assets/leadership-gap-bg.webp";
const leadershipGapBgAsset = { url: leadershipGapBgAssetSrc };
import coachesAssetSrc from "@/assets/devpro-team-new.png";
const coachesAsset = { url: coachesAssetSrc };
import gapAssessmentAssetSrc from "@/assets/gap-assessment.png";
const gapAssessmentAsset = { url: gapAssessmentAssetSrc };
import boardroomAssetSrc from "@/assets/boardroom.png";
const boardroomAsset = { url: boardroomAssetSrc };

import { Section, SectionHeader } from "@/components/site/Section";
import { NumberedGaps } from "@/components/site/NumberedGaps";
import { CountUp } from "@/components/site/CountUp";
import { motion, useReducedMotion } from "framer-motion";
import { gapContainer, gapItem, gapItemStatic, gapViewport } from "@/components/site/gap-motion";

import { FeaturedCoursesCarousel } from "@/components/site/FeaturedCoursesCarousel";
import { PlainDividedList } from "@/components/site/PlainDividedList";
import { KeywordAvatars } from "@/components/site/KeywordAvatars";
import { Package, Clock, SlidersHorizontal, Handshake } from "lucide-react";
import { SITE_URL } from "@/lib/seo";
import { MessageBand } from "@/components/site/MessageBand";

export const Route = createFileRoute("/")({
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/` }],
    meta: [
      { title: "Leadership Training for Growing Companies | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Live, practical leadership training your employees can use immediately. Built for 50 to 1000 person companies with no training department.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        property: "og:title",
        content: "Leadership Training for Growing Companies | Leadership Edge Live",
      },
      {
        property: "og:description",
        content:
          "Live, practical leadership training your employees can use immediately. Built for 50 to 1000 person companies with no training department.",
      },
      {
        property: "og:image",
        content:
          "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/4c83022e-2c72-4bbb-8e6b-f56d79dc9c0d/id-preview-c298fba0--8fe8f335-c532-48cb-8335-6e77200b9e20.lovable.app-1784497050375.png",
      },
      {
        name: "twitter:image",
        content:
          "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/4c83022e-2c72-4bbb-8e6b-f56d79dc9c0d/id-preview-c298fba0--8fe8f335-c532-48cb-8335-6e77200b9e20.lovable.app-1784497050375.png",
      },
    ],
  }),
  component: HomePage,
});

const gaps = [
  {
    title: "You are a great worker...now lead!",
    body: "They know the job. Nobody showed them how to lead the people doing it.",
    course: "new-leader-boot-camp",
  },
  {
    title: "Accountability depends on the manager",
    body: "Expectations shift by shift and location. Your people notice, and performance follows.",
    course: "performance-management",
  },
  {
    title: "Hard conversations happen too late",
    body: "Small issues get avoided until they become turnover, morale, or performance problems.",
    course: "on-the-spot-development-conversations",
  },
  {
    title: "Training that never changes behavior",
    body: "Courses get completed. Nothing looks different on Monday when pressure returns.",
    course: "development-to-delegation-cycle",
  },
  {
    title: "HR is carrying it alone",
    body: "The HR team cannot run hiring, compliance, and leadership development at once.",
    course: "developing-others-your-top-priority",
  },
  {
    title: "Change stalls in the middle",
    body: "Leaders set direction, but struggle to turn it into action.",
    course: "leading-change",
  },
];

const smb = [
  {
    title: "Nothing to build",
    body: "The curriculum, facilitators, and application assignments are already here.",
    icon: Package,
  },
  {
    title: "Fits the workday",
    body: "30-minute micro-modules taught once per week keeps managers on the floor.",
    icon: Clock,
  },
  {
    title: "Yours to adjust",
    body: "Customize application assignments to match your business.",
    icon: SlidersHorizontal,
  },
  {
    title: "A strategic learning partner",
    body: "A Development Pro focuses on your organization's needs.",
    icon: Handshake,
  },
];

function HomePage() {
  const reduceMotion = useReducedMotion();
  const gapMotionItem = reduceMotion ? gapItemStatic : gapItem;

  return (
    <>
      <MessageBand
        heroImage={homeHeroPhotoSrc}
        className="pt-24 pb-10 md:pt-28 md:pb-16"
        headline={
          <>
            <span className="font-light">Your best workers are now your newest leaders.</span>{" "}
            <span className="font-semibold">Nobody taught them how to lead.</span>
          </>
        }
        body="Thirty minutes a week, live. A real assignment back on their floor. And someone who follows up to make sure they did it."
        meta="Live sessions · 77+ leadership courses · No training department required"
        cta={{ label: "View Course Catalog", to: "/course-catalog/" }}
        secondaryCta={{ label: "Start a Free Pilot", to: "/pilot/" }}
      />

      <Section
        tone="image"
        backgroundImage={leadershipGapBgAsset.url}
        overlayClassName="bg-neutral-950/[0.85] md:bg-neutral-950/75"
        paddingClassName="pt-10 pb-10 md:pt-14 md:pb-14"
      >
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={gapViewport}
          variants={gapContainer}
        >
          <motion.div
            variants={gapMotionItem}
            className="mb-10 grid items-start gap-8 md:mb-12 md:grid-cols-[3fr_2fr]"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <h2
              className="text-3xl font-semibold leading-tight text-white md:text-4xl"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Common leadership gaps that impact results
            </h2>

            <p className="text-[1.1875rem] leading-relaxed text-white/80 md:pt-2 md:text-base">
              Yesterday's strengths do not solve tomorrow's problems
            </p>
          </motion.div>

          <NumberedGaps items={gaps} itemVariants={gapMotionItem} />

          <motion.div
            variants={gapContainer}
            className="mt-24 md:mt-28 grid gap-6 md:grid-cols-2"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <motion.div
              variants={gapMotionItem}
              className="rounded-2xl border border-white/15 bg-white/10 p-7 backdrop-blur-sm"
            >
              <p className="text-5xl font-semibold leading-none text-white md:text-6xl">
                <CountUp value={70} suffix="%" />
              </p>
              <p className="mt-4 text-[1.1875rem] leading-relaxed text-white md:text-base">
                of the variance in team engagement traces back to the manager.
              </p>
              <p className="mt-3 text-xs uppercase tracking-wider text-white/70">
                Source: Gallup, State of the American Manager, 2015
              </p>
            </motion.div>
            <motion.div
              variants={gapMotionItem}
              className="rounded-2xl border border-white/15 bg-white/10 p-7 backdrop-blur-sm"
            >
              <p className="text-5xl font-semibold leading-none text-white md:text-6xl">
                <CountUp value={2} suffix="x" />
              </p>
              <p className="mt-4 text-[1.1875rem] leading-relaxed text-white md:text-base">
                salary: replacing one employee costs one-half to two times what you paid them.
              </p>
              <p className="mt-3 text-xs uppercase tracking-wider text-white/70">
                Source: Gallup, 2019
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            variants={gapMotionItem}
            className="mt-10 flex justify-center pb-6 md:mt-12 md:pb-10"
          >
            <BusinessCaseButton onDark />
          </motion.div>
        </motion.div>
      </Section>

      <MessageBand
        tone="lavender"
        unboxed
        headlineClassName="text-3xl font-semibold md:text-4xl"
        headline="Training combined with accountability and support is a winning approach."
        body="Thirty minutes live each week. A real assignment with their own team. And a Development Pro who calls them when they go quiet."
        meta="Live facilitators · Workplace assignments · One Development Pro who knows your people"
      />

      <FeaturedCoursesCarousel />

      <Section tone="default" paddingClassName="pt-12 pb-20 md:pt-16 md:pb-28">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={gapViewport}
          variants={gapContainer}
        >
          <motion.div variants={gapMotionItem}>
            <SectionHeader
              title="You don't need a training department to get started"
              titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
              titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
            />
          </motion.div>
          <PlainDividedList
            items={smb}
            itemVariants={gapMotionItem}
            bodyClassName="text-[1.1875rem] md:text-base"
          />
          <motion.div variants={gapMotionItem} className="mt-10 flex justify-center">
            <Link
              to="/faq/"
              className="inline-flex items-center rounded-[6px] border border-primary px-6 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/5"
            >
              Read the FAQ
            </Link>
          </motion.div>
        </motion.div>
      </Section>

      <MessageBand
        eyebrow="Start with ten people"
        headline="Pick ten managers. We'll train them for four weeks, free."
        headlineClassName="text-3xl font-semibold md:text-4xl"
        badge="Free"
        body="Live sessions, real assignments with their own teams, and a Development Pro following up with each one. No card, no contract. Then you decide."
        cta={{ label: "Start your pilot", to: "/pilot/" }}
      />

      <KeywordAvatars />

      <section
        className="relative z-10 mt-6 md:mt-24"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
          <img
            src={boardroomAsset.url}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={gapViewport}
          variants={gapContainer}
          className="relative z-[1]"
        >
          <div className="flex flex-col justify-center gap-7 bg-[#693D94]/[0.91] px-6 py-[83px] text-primary-foreground md:px-10 md:py-[104px] lg:pr-[12rem]">
            <motion.div
              variants={gapMotionItem}
              className="flex w-fit flex-col items-center text-center mx-auto lg:ml-[250px] md:max-w-[28rem]"
            >
              <h2
                className="font-sans text-2xl font-semibold leading-tight text-primary-foreground md:text-3xl"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                Leadership Gaps Assessment in five minutes.
              </h2>
              <p className="mt-4 text-[1.1875rem] leading-relaxed text-primary-foreground/80 md:text-base">
                Most leadership problems trace back to a few specific gaps. This assessment shows
                you exactly which ones are costing you.
              </p>
              <Link
                to="/leadership-gaps-assessment/"
                className="mt-7 inline-flex w-fit items-center rounded-[6px] bg-background px-6 py-3 text-sm font-semibold text-primary shadow-brand transition-transform hover:-translate-y-0.5"
              >
                Take assessment
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <div className="pointer-events-none absolute left-[calc(50%+320px)] top-1/2 z-10 hidden h-[125%] -translate-x-1/2 -translate-y-1/2 lg:block">
          <motion.img
            src={gapAssessmentAsset.url}
            alt="Leadership Gaps Assessment results on a phone"
            animate={reduceMotion ? undefined : { y: [0, -12, 0] }}
            transition={
              reduceMotion ? undefined : { duration: 5, repeat: Infinity, ease: "easeInOut" }
            }
            className="h-full w-auto drop-shadow-2xl"
          />
        </div>
      </section>

      <PageHero
        headingLevel="h2"
        imageGroupScale={1.236}
        shapeColor="#693D94"
        shapeOpacity="0.04"
        midShapeOpacity="0.06"
        pentagonColor="#A08BBE"
        align="center"
        headline={
          <span className="mx-auto block max-w-[22ch] text-3xl font-semibold leading-[1.12] pb-[0.12em] text-ink text-balance md:text-4xl lg:mt-10 lg:max-w-none">
            Meet Your Learning Team
          </span>
        }
        images={[
          { src: heroPeopleAsset.url, alt: "Smiling leader in a black suit and green plaid tie" },
          { src: heroPeople2Asset.url, alt: "Josh Hoganson, Development Pro" },
          { src: heroPeople3Asset.url, alt: "Colleen James, Development Pro" },
          {
            src: heroPeople4Asset.url,
            alt: "Nancy Duenas, Director of Instructional Design and Quality",
          },
          { src: muhammadSrc, alt: "Muhammad Atif, Graphic Designer" },
          { src: aimeeSrc, alt: "Aimee Keogh, Director of Operations" },
          { src: alveeraSrc, alt: "Alveera D'Souza, Project Manager" },
          { src: ayushiSrc, alt: "Ayushi Gupta, Instructional Designer" },
          { src: courtneySrc, alt: "Courtney Leligdon, Instructional Designer" },
          { src: jakaSrc, alt: "Jaka Kalena, Creative Editor and Animator" },
          { src: pendiSrc, alt: "Pendi Cahyono, Creative Editor and Animator" },
          { src: kanniSrc, alt: "Chaitanya Kanni, Instructional Designer" },
          { src: nischithaSrc, alt: "Nischitha M, Social Media Manager" },
          { src: jonathanSrc, alt: "Jonathan Fuentes, Development Pro" },
          { src: patSrc, alt: "Patrick Carvalho, Director of Creative Services" },
          { src: anitaSrc, alt: "Anita D'Souza, Project Manager" },
          { src: sushilSrc, alt: "Sushil Bhaval, Website Developer" },
          { src: derekSrc, alt: "Derek Roeder, Development Pro" },
        ]}
        personLabels={[
          { name: "Shawn Snyder", title: "Chief Learning Strategist" },
          { name: "Josh Hoganson", title: "Development Pro" },
          { name: "Colleen James", title: "Development Pro" },
          { name: "Nancy Duenas", title: "Director of Instructional Design and Quality" },
          { name: "Muhammad Atif", title: "Graphic Designer" },
          { name: "Aimee Keogh", title: "Director of Operations" },
          { name: "Alveera D'Souza", title: "Project Manager" },
          { name: "Ayushi Gupta", title: "Instructional Designer" },
          { name: "Courtney Leligdon", title: "Instructional Design & Learning Experience Manager" },
          { name: "Jaka Kalena", title: "Creative Editor/Animator" },
          { name: "Pendi Cahyono", title: "Creative Editor/Animator" },
          { name: "Chaitanya Kanni", title: "Instructional Designer" },
          { name: "Nischitha M", title: "Social Media Manager" },
          { name: "Jonathan Fuentes", title: "Development Pro" },
          { name: "Patrick Carvalho", title: "Director of Creative Services" },
          { name: "Anita D'Souza", title: "Project Manager" },
          { name: "Sushil Bhaval", title: "Website Developer" },
          { name: "Derek Roeder", title: "Development Pro" },
        ]}
        showKeywordLabels={false}
        rotatingQuotes={[
          "We love helping people recognize that leading others isn't about having all the answers... it's about bringing out the best in themselves and others.",
          "The most rewarding part of Leadership Edge Live is watching someone discover a strength they didn't realize they had. We see it in their growth during their bridgework application assignments.",
          "Growth becomes meaningful when learning changes what someone does in a real conversation, decision, or challenge.",
          "We believe every leader deserves practical support, not just information, but the opportunity to practice, reflect, and grow. Leadership Edge Live plus support from a coach is very powerful.",
          "There's something powerful about seeing a hesitant new leader become a confident leader who knows how to support their team.",
          "We love creating those moments when a concept clicks and someone immediately sees how they can use it at work.",
          "Growth doesn't stop with one person. When a leader changes how they communicate, listen, and build trust, the entire team feels it.",
          "Our goal is to make leadership development feel relevant, approachable, and possible for everyone.",
          "We're passionate about helping technically talented people develop the human skills they need to interact with others well.",
          "We don't believe development should end when the course is over. The real growth begins when leaders put what they learned into practice.",
          "Every person brings different strengths, experiences, and possibilities. We love helping them find the person-centered approach that works for who they are.",
          "It's incredibly fulfilling to help someone become more self-aware, more confident, and more intentional about the impact they have on others.",
          "We help leaders navigate real challenges because growth rarely happens in theory; it happens in the moments that matter at work.",
          "At Leadership Edge Live, we believe developing leaders is one of the most meaningful ways to strengthen people, teams, and organizations.",
          "What we love most is knowing that when we help one person grow, that growth continues through every person they lead.",
          "Hard conversations get easier with practice. We give people a safe place to try one, get feedback, and try again.",
          "We design every session to fit into the real workday, so what someone learns on Tuesday is already helping their team by Friday.",
          "A good idea only matters if it gets used. That's why we follow up, so new leaders actually do what they said they would.",
        ]}
        ctas={[
          { label: "View the Course Catalog", to: "/course-catalog/", variant: "primary" },
          { label: "Talk to a Development Pro", to: "/contact/", variant: "secondary" },
        ]}
      />
    </>
  );
}
